import React, { useState, useEffect, useMemo } from 'react';
import { useMandantStore } from '../../../store/mandantStore';
import { TATBESTAND_MAP } from '../data/tatbestaende';
import {
  loadFaelligkeiten, saveFaelligkeit, deleteFaelligkeit,
  markErledigtMitFolgetermin, nextMaKennung, loadArbeitsplaetze,
  type VguFaelligkeit,
} from '../../../services/vorsorgeService';
import type { Arbeitsplatz } from './ArbeitsplatzVerwaltung';
import { KohortenView } from './KohortenView';
import { logAktion } from '../../../services/auditLogService';

// ── Status-Badge ──────────────────────────────────────────────────────────

const STATUS_CFG = {
  OFFEN:       { label: 'Fällig',      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-400' },
  UEBERFAELLIG:{ label: 'Überfällig',  color: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
  ERLEDIGT:    { label: 'Erledigt',    color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400' },
};

// ── Neuer Eintrag-Form ────────────────────────────────────────────────────

function NeuerEintragForm({ mandantId, arbeitsplaetze, onSaved, onCancel }: {
  mandantId:     string;
  arbeitsplaetze: Arbeitsplatz[];
  onSaved:       () => void;
  onCancel:      () => void;
}): React.JSX.Element {
  const [kennung,    setKennung]    = useState('');
  const [apId,       setApId]       = useState(arbeitsplaetze[0]?.id ?? '');
  const [tatCode,    setTatCode]    = useState('');
  const [datum,      setDatum]      = useState('');
  const [generating, setGenerating] = useState(false);

  const ap = arbeitsplaetze.find(a => a.id === apId);
  const verfuegbareTats = ap !== undefined
    ? ap.tatbestandCodes.map(c => ({ code: c, bez: TATBESTAND_MAP.get(c)?.bezeichnung ?? c }))
    : [];

  useEffect(() => {
    if (tatCode === '' && verfuegbareTats.length > 0) {
      setTatCode(verfuegbareTats[0]?.code ?? '');
    }
  }, [apId]);

  useEffect(() => {
    if (kennung !== '') return;
    setGenerating(true);
    void nextMaKennung(mandantId).then(k => { setKennung(k); setGenerating(false); });
  }, []);

  const handleSave = (): void => {
    if (!kennung || !apId || !tatCode || !datum) return;
    const today = new Date().toISOString().slice(0, 10);
    const f: VguFaelligkeit = {
      id:                  crypto.randomUUID(),
      mandantId,
      arbeitsplatzId:      apId,
      mitarbeiterKennung:  kennung,
      tatbestandCode:      tatCode,
      faelligkeitDatum:    datum,
      status:              datum < today ? 'UEBERFAELLIG' : 'OFFEN',
      erstelltAm:          new Date().toISOString(),
      geaendertAm:         new Date().toISOString(),
    };
    void saveFaelligkeit(f).then(onSaved);
  };

  return (
    <div className="card space-y-4 border-blue-100 dark:border-blue-900/50 bg-blue-50/30 dark:bg-blue-950/10">
      <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Neue VGÜ-Fälligkeit eintragen</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="label-text">Mitarbeiter-Kennung</label>
          <input value={generating ? '…' : kennung}
            onChange={e => { setKennung(e.target.value); }}
            placeholder="MA-001" className="input-field text-sm font-mono" />
          <p className="text-xs text-gray-400">Pseudonym — kein Klarname!</p>
        </div>
        <div className="space-y-1">
          <label className="label-text">Fälligkeitsdatum</label>
          <input type="date" value={datum} onChange={e => { setDatum(e.target.value); }}
            className="input-field text-sm" />
        </div>
      </div>
      <div className="space-y-1">
        <label className="label-text">Arbeitsplatz</label>
        <select value={apId} onChange={e => { setApId(e.target.value); setTatCode(''); }}
          className="input-field text-sm">
          {arbeitsplaetze.map(a => (
            <option key={a.id} value={a.id}>{a.bezeichnung}{a.abteilung ? ` (${a.abteilung})` : ''}</option>
          ))}
        </select>
      </div>
      {verfuegbareTats.length > 0 ? (
        <div className="space-y-1">
          <label className="label-text">VGÜ-Tatbestand</label>
          <select value={tatCode} onChange={e => { setTatCode(e.target.value); }}
            className="input-field text-sm">
            {verfuegbareTats.map(t => (
              <option key={t.code} value={t.code}>{t.code} — {t.bez}</option>
            ))}
          </select>
        </div>
      ) : (
        <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 rounded p-2">
          ⚠ Diesem Arbeitsplatz sind noch keine Tatbestände zugeordnet (→ Tab Arbeitsplätze).
        </p>
      )}
      <div className="flex gap-2">
        <button onClick={handleSave}
          disabled={!kennung || !apId || !tatCode || !datum || verfuegbareTats.length === 0}
          className="btn-primary text-sm disabled:opacity-50">Speichern</button>
        <button onClick={onCancel} className="btn-secondary text-sm">Abbrechen</button>
      </div>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

type FFilter = 'alle' | 'OFFEN' | 'UEBERFAELLIG' | 'ERLEDIGT';

export function FaelligkeitenListe(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [faelligkeiten,  setFaelligkeiten]  = useState<VguFaelligkeit[]>([]);
  const [arbeitsplaetze, setArbeitsplaetze] = useState<Arbeitsplatz[]>([]);
  const [view, setView]       = useState<'tabelle' | 'kohorten'>('tabelle');
  const [filter, setFilter]   = useState<FFilter>('alle');
  const [search, setSearch]   = useState('');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]  = useState(true);
  const [erledigungId, setErledigungId]   = useState<string | null>(null);
  const [erledigungDatum, setErledigungDatum] = useState(new Date().toISOString().slice(0, 10));

  const reload = (): void => {
    void Promise.all([
      loadFaelligkeiten(mandant?.id),
      loadArbeitsplaetze(mandant?.id),
    ]).then(([f, a]) => {
      setFaelligkeiten(f);
      setArbeitsplaetze(a);
      setLoading(false);
    });
  };

  useEffect(() => {
    setLoading(true);
    reload();
  }, [mandant?.id]);

  const filtered = useMemo(() => faelligkeiten.filter(f => {
    const matchStatus = filter === 'alle' || f.status === filter;
    const q = search.toLowerCase();
    const matchSearch = q === ''
      || f.mitarbeiterKennung.toLowerCase().includes(q)
      || f.tatbestandCode.toLowerCase().includes(q)
      || (TATBESTAND_MAP.get(f.tatbestandCode)?.bezeichnung.toLowerCase().includes(q) ?? false);
    return matchStatus && matchSearch;
  }).sort((a, b) => a.faelligkeitDatum.localeCompare(b.faelligkeitDatum)), [faelligkeiten, filter, search]);

  const counts = {
    OFFEN:        faelligkeiten.filter(f => f.status === 'OFFEN').length,
    UEBERFAELLIG: faelligkeiten.filter(f => f.status === 'UEBERFAELLIG').length,
    ERLEDIGT:     faelligkeiten.filter(f => f.status === 'ERLEDIGT').length,
  };

  const handleErledigungConfirm = (id: string): void => {
    const f = faelligkeiten.find(x => x.id === id);
    void markErledigtMitFolgetermin(id, erledigungDatum).then(naechste => {
      if (f !== undefined) {
        void logAktion(mandant?.name ?? f.mandantId, 'VGU_ERLEDIGT', {
          tatbestand: f.tatbestandCode,
          kennung:    f.mitarbeiterKennung,
          datum:      erledigungDatum,
        });
      }
      setErledigungId(null);
      reload();
      if (naechste !== null) {
        // kurze Bestätigung über window.confirm ist zu invasiv — einfach neu laden
      }
    });
  };

  const handleDelete = (id: string): void => {
    if (!window.confirm('Eintrag löschen?')) return;
    void deleteFaelligkeit(id).then(reload);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400">Lade Fälligkeiten…</div>;
  }

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">VGÜ-Fälligkeiten</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Pseudonymisiert (DSGVO-konform) ·
            {mandant !== null && mandant !== undefined
              ? <span className="ml-1 font-medium" style={{ color: mandant.farbe }}>{mandant.name}</span>
              : ' alle Mandanten'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
            <button onClick={() => { setView('tabelle'); }}
              className={`px-3 py-1.5 transition-colors ${view === 'tabelle' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              📋 Tabelle
            </button>
            <button onClick={() => { setView('kohorten'); }}
              className={`px-3 py-1.5 transition-colors border-l border-gray-200 dark:border-gray-700 ${view === 'kohorten' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              📅 Kohorten
            </button>
          </div>
          <button onClick={() => { window.print(); }} className="btn-secondary text-sm" data-no-print>
            🖨 Vorsorgeplan
          </button>
          <button onClick={() => { setShowForm(true); }} className="btn-primary text-sm"
            disabled={arbeitsplaetze.length === 0}>
            + Fälligkeit eintragen
          </button>
        </div>
      </div>

      {arbeitsplaetze.length === 0 && (
        <div className="card bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400 p-4">
          ⚠ Zuerst Arbeitsplätze anlegen (Tab „Arbeitsplätze"), dann Fälligkeiten eintragen.
        </div>
      )}

      {/* KPI-Chips */}
      <div className="flex gap-2 flex-wrap">
        {([
          { key: 'alle'        as FFilter, label: `Alle (${faelligkeiten.length})`,       color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
          { key: 'UEBERFAELLIG' as FFilter, label: `Überfällig (${counts.UEBERFAELLIG})`, color: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400' },
          { key: 'OFFEN'        as FFilter, label: `Fällig (${counts.OFFEN})`,             color: 'bg-yellow-100 text-yellow-800' },
          { key: 'ERLEDIGT'     as FFilter, label: `Erledigt (${counts.ERLEDIGT})`,        color: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400' },
        ]).map(chip => (
          <button key={chip.key}
            onClick={() => { setFilter(chip.key); }}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all
              ${filter === chip.key ? 'ring-2 ring-offset-1 ring-blue-500 ' : ''}${chip.color}`}>
            {chip.label}
          </button>
        ))}
      </div>

      {/* Neuer-Eintrag-Form */}
      {showForm && mandant !== null && mandant !== undefined && (
        <NeuerEintragForm
          mandantId={mandant.id}
          arbeitsplaetze={arbeitsplaetze}
          onSaved={() => { setShowForm(false); reload(); }}
          onCancel={() => { setShowForm(false); }}
        />
      )}

      {/* Kohorten-Ansicht */}
      {view === 'kohorten' && (
        <KohortenView faelligkeiten={faelligkeiten} />
      )}

      {/* Tabellen-Ansicht */}
      {view === 'tabelle' && (<>

      {/* Suche */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input type="search" value={search} onChange={e => { setSearch(e.target.value); }}
          placeholder="Kennung, Tatbestand…"
          className="input-field pl-9 text-sm" />
      </div>

      {/* Tabelle */}
      {filtered.length === 0 ? (
        <div className="card text-center py-12 space-y-2">
          <p className="text-3xl">🩺</p>
          <p className="font-medium text-gray-600 dark:text-gray-400">
            {faelligkeiten.length === 0
              ? 'Noch keine Fälligkeiten eingetragen'
              : 'Keine Einträge für diesen Filter'
            }
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Kennung</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden sm:table-cell">Arbeitsplatz</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Tatbestand</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Fälligkeit</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden md:table-cell">Status</th>
                <th className="w-20 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map(f => {
                const ap  = arbeitsplaetze.find(a => a.id === f.arbeitsplatzId);
                const tat = TATBESTAND_MAP.get(f.tatbestandCode);
                const sc  = STATUS_CFG[f.status];
                const isUeber = f.status === 'UEBERFAELLIG';
                return (
                  <tr key={f.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors
                    ${isUeber ? 'bg-red-50/30 dark:bg-red-950/10' : ''}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-bold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                        {f.mitarbeiterKennung}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden sm:table-cell text-xs">
                      {ap?.bezeichnung ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 px-1.5 py-0.5 rounded">
                        {f.tatbestandCode}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 hidden lg:inline">{tat?.bezeichnung}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {new Date(f.faelligkeitDatum + 'T00:00:00').toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {f.letztesDatum !== undefined && (
                        <p className="text-xs text-gray-400 font-normal">
                          Letzte: {new Date(f.letztesDatum + 'T00:00:00').toLocaleDateString('de-AT')}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.color}`}>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-2 py-3">
                      {erledigungId === f.id ? (
                        <div className="flex items-center gap-1 min-w-0">
                          <input type="date"
                            value={erledigungDatum}
                            onChange={e => { setErledigungDatum(e.target.value); }}
                            className="input-field text-xs py-0.5 px-1 w-28 flex-shrink-0" />
                          <button onClick={() => { handleErledigungConfirm(f.id); }}
                            className="p-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex-shrink-0"
                            title="Bestätigen">✓</button>
                          <button onClick={() => { setErledigungId(null); }}
                            className="p-1 text-xs text-gray-400 hover:text-gray-600 flex-shrink-0"
                            title="Abbrechen">✕</button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          {f.status !== 'ERLEDIGT' && (
                            <button
                              onClick={() => {
                                setErledigungDatum(new Date().toISOString().slice(0, 10));
                                setErledigungId(f.id);
                              }}
                              className="p-1.5 text-xs text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30 rounded-lg transition-colors"
                              title="Erledigt + Folgetermin anlegen">✓</button>
                          )}
                          <button onClick={() => { handleDelete(f.id); }}
                            className="p-1.5 text-xs text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                            title="Löschen">🗑</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        ✅ Pseudonymisiert · keine Klarnamen · DSGVO-konform · Dexie v5
      </p>
      </>)}
    </div>
  );
}
